import { api } from './api'
import { 
  CreateFeedback, 
  Feedback, 
  FeedbackAnalytics,
  FeedbackStats,
  FeedbackWithDetails 
} from '@shared/schemas/feedback'

export interface SubmitFeedbackRequest {
  messageId: string
  rating: number
  comment?: string
}

export interface FeedbackResponse {
  success: boolean
  data: Feedback
}

export interface FeedbackAnalyticsResponse {
  success: boolean
  data: FeedbackAnalytics
}

export interface FeedbackStatsResponse {
  success: boolean
  data: FeedbackStats
}

export interface RecentFeedbackResponse {
  success: boolean
  data: FeedbackWithDetails[]
}

export interface DeleteFeedbackResponse {
  success: boolean
  message: string
}

/**
 * Feedback service class for feedback-related operations
 */
export class FeedbackService {
  private static readonly FEEDBACK_ENDPOINTS = {
    FEEDBACK: '/feedback',
    MESSAGE_FEEDBACK: (messageId: string) => `/feedback/message/${messageId}`,
    ANALYTICS: '/feedback/analytics',
    AGENT_STATS: (agentType?: string) => agentType ? `/feedback/stats/${agentType}` : '/feedback/stats',
    RECENT_FEEDBACK: '/feedback/recent',
    DELETE_FEEDBACK: (messageId: string, userId: string) => `/feedback/${messageId}/${userId}`,
  } as const

  /**
   * Submit or update feedback for a message
   */
  static async submitFeedback(request: SubmitFeedbackRequest): Promise<Feedback> {
    const response = await api.post<FeedbackResponse>(this.FEEDBACK_ENDPOINTS.FEEDBACK, request)
    return response.data
  }

  /**
   * Get current user's feedback for a specific message
   */
  static async getMessageFeedback(messageId: string): Promise<Feedback | null> {
    try {
      const response = await api.get<FeedbackResponse>(
        this.FEEDBACK_ENDPOINTS.MESSAGE_FEEDBACK(messageId)
      )
      return response.data
    } catch (error: any) {
      // If feedback doesn't exist, return null instead of throwing
      if (error.status === 404) {
        return null
      }
      throw error
    }
  }

  /**
   * Get comprehensive feedback analytics (admin only)
   */
  static async getAnalytics(): Promise<FeedbackAnalytics> {
    const response = await api.get<FeedbackAnalyticsResponse>(this.FEEDBACK_ENDPOINTS.ANALYTICS)
    return response.data
  }

  /**
   * Get feedback statistics for a specific agent type (admin only)
   */
  static async getAgentStats(agentType?: 'HR' | 'OPS'): Promise<FeedbackStats> {
    const response = await api.get<FeedbackStatsResponse>(
      this.FEEDBACK_ENDPOINTS.AGENT_STATS(agentType)
    )
    return response.data
  }

  /**
   * Get recent feedback with optional filters (admin only)
   */
  static async getRecentFeedback(options?: {
    agentType?: 'HR' | 'OPS'
    limit?: number
    includeComments?: boolean
  }): Promise<FeedbackWithDetails[]> {
    const params = new URLSearchParams()
    
    if (options?.agentType) {
      params.append('agentType', options.agentType)
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString())
    }
    if (options?.includeComments !== undefined) {
      params.append('includeComments', options.includeComments.toString())
    }

    const url = `${this.FEEDBACK_ENDPOINTS.RECENT_FEEDBACK}${params.toString() ? `?${params.toString()}` : ''}`
    const response = await api.get<RecentFeedbackResponse>(url)
    return response.data
  }

  /**
   * Delete feedback (admin only)
   */
  static async deleteFeedback(messageId: string, userId: string): Promise<void> {
    await api.delete<DeleteFeedbackResponse>(
      this.FEEDBACK_ENDPOINTS.DELETE_FEEDBACK(messageId, userId)
    )
  }
}

// Export for consistent naming
export const feedbackService = FeedbackService
export default FeedbackService